'use client';
/* eslint-disable @next/next/no-img-element -- the white wordmark PNG is a /public asset shown on the dark drawer */
import { useEffect, useRef, useState } from 'react';
import {
  ArrowUpRightIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ShoppingCartIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { brandAssetAt } from 'helpers/brandAssets';
import BrandImage from './brand-image';

export interface MobileNavItem {
  label: string;
  href?: string;
}

export interface MobileMenuGroup {
  /** Optional group heading shown as an eyebrow; omit for a flat, ungrouped list. */
  title?: string;
  items: MobileNavItem[];
}

interface MobileMenuDrawerProps {
  open: boolean;
  onClose: () => void;
  groups: MobileMenuGroup[];
  query: string;
  onQueryChange: (value: string) => void;
  onSearchSubmit: (e: React.FormEvent) => void;
  /** Open straight into the search view (from the mobile bar's search icon). */
  startInSearch?: boolean;
  /** Bottom-bar cart action (closes this drawer and opens the cart). */
  onCartClick: () => void;
  cartCount: number;
  samplesHref?: string;
  /** Marketing-site link for the current section; replaces the generic exit link when present. */
  experience?: { label: string; href: string };
  /** Image cards under the root list — the lookbook plus one pick for the current section. */
  callouts?: MobileMenuCallout[];
}

export interface MobileMenuCallout {
  label: string;
  href: string;
  image: string;
  external?: boolean;
}

const MobileMenuDrawer = ({
  open,
  onClose,
  groups,
  query,
  onQueryChange,
  onSearchSubmit,
  startInSearch,
  onCartClick,
  cartCount,
  samplesHref = '/samples/',
  experience,
  callouts = [],
}: MobileMenuDrawerProps) => {
  // view: the grouped root list or the search field.
  const [searchView, setSearchView] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync the view to how the drawer was opened; reset when it closes.
  useEffect(() => {
    setSearchView(open ? !!startInSearch : false);
  }, [open, startInSearch]);

  useEffect(() => {
    if (searchView) searchInputRef.current?.focus();
  }, [searchView]);

  return (
    <div
      className={`on-dark fixed inset-0 z-[60] flex flex-col bg-[#212121] font-gt text-white transition-transform duration-300 ease-out xl:hidden ${
        open ? 'translate-x-0' : 'translate-x-full'
      }`}
      aria-hidden={!open}
    >
      {/* Drawer header — logo; search toggle + close. */}
      <div className="flex h-64 shrink-0 items-center justify-between border-b border-white/10 px-24">
          <div className="flex items-center gap-16">
            <a href="https://www.viewrail.com" aria-label="Viewrail" className="flex min-h-44 items-center">
              <img src={brandAssetAt('logo-wordmark.png', 284)} alt="Viewrail" width={142} height={17} />
            </a>
            <span className="h-24 w-px bg-white/40" aria-hidden />
            <a href="/" className="flex min-h-44 items-center text-12">
              SHOP
            </a>
          </div>

        <div className="flex items-center gap-2">
          {!searchView && (
            <button
              aria-label="Search"
              onClick={() => setSearchView(true)}
              className="flex size-44 items-center justify-center transition-opacity hover:opacity-70"
            >
              <MagnifyingGlassIcon className="size-22" />
            </button>
          )}
          <button aria-label="Close menu" onClick={onClose} className="-mr-11 flex size-44 items-center justify-center transition-opacity hover:opacity-70">
            <XMarkIcon className="size-24" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-24">
        {searchView ? (
          <form onSubmit={onSearchSubmit} className="flex items-center gap-16">
            <MagnifyingGlassIcon className="size-24 shrink-0 text-white/60" />
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search shop.viewrail.com..."
              className="w-full appearance-none border-0 bg-transparent text-20 text-white outline-none placeholder:text-white/50"
            />
          </form>
        ) : (
          // Root: grouped nav (a titled group renders an eyebrow; an untitled one is a flat list).
          <div className="flex flex-col gap-32">
            {groups.map((group, groupIndex) => (
              <div key={group.title ?? groupIndex}>
                {group.title && <p className="text-12 font-bold uppercase text-white/50">{group.title}</p>}
                <ul className={`flex flex-col ${group.title ? 'mt-8' : ''}`}>
                  {group.items.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href ?? '#'}
                        className="flex w-full items-center justify-between py-10 text-20 font-medium transition-opacity hover:opacity-70"
                      >
                        {item.label}
                        <ChevronRightIcon className="size-20" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Featured callouts — white-bordered image cards filling
                the space under the short root list: the lookbook, then a sample or kit for the section, stacked
                full-width like the reference. */}
            {callouts.length > 0 && (
              <div className="flex flex-col gap-24 pb-24">
                {callouts.map((callout) => (
                  <a
                    key={callout.label}
                    href={callout.href}
                    {...(callout.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="group block"
                  >
                    <div className="aspect-[2/1] w-full overflow-hidden border border-white/40 bg-white/10">
                      <BrandImage src={callout.image} alt="" width={800} className="size-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <p className="mt-12 flex items-center gap-8 text-18 font-medium leading-[1.2]">
                      {callout.label}
                      {callout.external ? <ArrowUpRightIcon className="size-14 shrink-0" /> : <ChevronRightIcon className="size-14 shrink-0" />}
                    </p>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Exit to the marketing site: the section's experience page when there is one, else the home. */}
      <a
        href={experience?.href ?? 'https://www.viewrail.com'}
        target="_blank"
        rel="noopener noreferrer"
        className="flex shrink-0 items-center gap-6 px-24 py-16 text-14 text-white/50 transition-opacity hover:opacity-70"
      >
        {experience?.label ?? 'Back to the main website'}
        <ArrowUpRightIcon className="size-14" />
      </a>

      {/* Bottom bar — samples left; account + cart right (white dot on the cart when populated). The
          phone header has no account control, so this is the only route to /account below lg. */}
      <div className="flex shrink-0 items-center justify-between border-t border-white/10 p-24 text-16 font-medium">
        <a
          href={samplesHref}
          className="inline-flex h-44 items-center justify-center bg-white px-24 font-gt text-14 font-medium text-[#212121] transition-opacity hover:opacity-90"
        >
          Get Samples
        </a>
        <div className="-my-10 -mr-10 flex items-center gap-4">
          <a href="/account/" aria-label="Account" className="flex size-44 items-center justify-center transition-opacity hover:opacity-70">
            <UserIcon className="size-24" />
          </a>
          <button onClick={onCartClick} aria-label="Cart" className="relative flex size-44 items-center justify-center transition-opacity hover:opacity-70">
            {cartCount > 0 && <span className="absolute right-7 top-8 size-8 rounded-full bg-white ring-2 ring-[#1c1c1c]" aria-hidden />}
            <ShoppingCartIcon className="size-24" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileMenuDrawer;
